import { avoidReloadOnBackNav, processOnRouteLeave } from '../domain/router/routeUtils'
import { requestPageAdConfig } from '../store/ad/ad.actions'
import { noticeNavigationDone } from '../store/history/history.actions'
import { selectCurrentHistoryRoute, selectIsCurrentHistoryRoutePageless } from '../store/history/history.selectors'
import { setHistoryRouteParams } from '../store/history/historySlice'
import { noop } from '../utils/function'
import RouteHandlingState from './RouteHandlingState'

/**
 * Each route has to implement 2 functions:
 * - onRouteEnter -> return a promise after executions of redux actions
 * - renderPage -> return JSX representation of the page
 * And can implement a third one:
 * - onRouteLeave -> execute actions when the page is left
 * */

export const handleRouteLoading =
  ({ onRouteEnter, renderPage, onRouteLeave }) =>
  ({ routeContext, params } = {}) => {
    if (__SERVER__ && !renderPage) throw Error('renderPage should always be defined on server')
    if (!onRouteEnter) console.warn('Be careful the function onRouteEnter has not been passed')

    const routeHandlingState = RouteHandlingState.getInstance()

    const { action, state, store, prevRoute } = routeContext // FIXME NES-1803
    const avoidFetchData =
      (state && state.avoidDispatchingAnyPageAction && action !== 'POP') ||
      (action === 'POP' && avoidReloadOnBackNav(prevRoute?.route))
    const avoidPageTag = state && state.avoidPageTag

    processOnRouteLeave({ routeContext, params, currentOnRouteLeave: onRouteLeave })

    const avoidRefetchingPageData = state && state.avoidRefetchingPageData
    const currentRouteIsNotPageless = !selectIsCurrentHistoryRoutePageless(store.getState())

    routeHandlingState.renderPage = renderPage

    let actionsToDispatch = Promise.resolve()

    store.dispatch(setHistoryRouteParams(params))

    if (__BROWSER__) {
      if (currentRouteIsNotPageless)
        requestPageAdConfig(selectCurrentHistoryRoute(store.getState()))(store.dispatch, store.getState)

      if (avoidFetchData) {
        if (!avoidPageTag) deferSendingPageTag(routeContext)()
      } else {
        onRouteEnter({ routeContext, store, params, state, avoidRefetchingPageData }).then(
          !avoidPageTag ? deferSendingPageTag(routeContext) : noop
        )
      }
    } else {
      actionsToDispatch = onRouteEnter({ routeContext, store, params, state, avoidRefetchingPageData })
    }

    return actionsToDispatch.then(() => ({
      component: routeHandlingState.renderPage({ routeContext, params, state })
    }))
  }

export const deferSendingPageTag = routeContext => {
  const { store } = routeContext
  const previousRoute = selectCurrentHistoryRoute(store.getState())
  return () => {
    const currentRoute = selectCurrentHistoryRoute(store.getState())

    if (previousRoute === currentRoute && __BROWSER__) {
      noticeNavigationDone(routeContext)(store.dispatch, store.getState)
    }
  }
}
